import { Alert, Button, Chip, DialogContent, DialogTitle, Divider, Modal, ModalClose, ModalDialog, ModalOverflow, Snackbar, Typography } from "@mui/joy"
import { useEffect, useState } from "react";
import { useAuth } from "../../context/authContext";
import { Report } from '@mui/icons-material';

import LoadingSpinnerComponent from "../LoadingSpinnerComponent";
import CreateAutomationPopup, { Automation } from "./CreateAutomationPopup";

const replykitNetworking = require('../../Networking/Reply Kit/ReplykitAPI');

export type Reply = {
    id: string;
    category: string;
    body: string;
    language: string;
    created_date: Date;
    is_active: boolean;
}

function ReplyAutomationsPopup({ subscription, app, didSaveNewAutomation, showAutomationsModal, onClose }: any) {
    const { token } = useAuth();

    const [showAddNewAutomationModal, setShowAddNewAutomationModal] = useState(false);
    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);
    const [success, setSuccess] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [automations, setAutomations] = useState<Automation[]>([]);

    useEffect(() => {
        if (subscription.plan === 'AAA' || subscription.plan === 'U') {
            getAutomations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getAutomations = () => {
        setLoading(true);

        replykitNetworking.getAutomationsOnKit(app.app.id, token).then((results: any) => {
            setAutomations(results);
            setLoading(false);
        }).catch((error: Error) => {
            setError("Failed to fetch predefined replies.");
            setShowError(true);
            setLoading(false);
        });
    }

    const disableAutomation = (automation: Automation) => {
        setLoading(true);

        replykitNetworking.disableAutomationOnKit(automation, token).then((results: any) => {
            setSuccess("Automation updated successfully. 🚀");
            setShowSuccess(true);
            setLoading(false);
            getAutomations();
            didSaveNewAutomation();
        }).catch((error: Error) => {
            setError("Failed to update automation.");
            setShowError(true);
            setLoading(false);
        });
    }

    const deleteAutomation = (automationId: string) => {
        setLoading(true);

        replykitNetworking.deleteAutomationOnKit(automationId, token).then((results: any) => {
            setSuccess("Automation deleted successfully. 🚀");
            setShowSuccess(true);
            setLoading(false);
            getAutomations();
            didSaveNewAutomation();
        }).catch((error: Error) => {
            setError("Failed to delete reply template.");
            setShowError(true);
            setLoading(false);
        });
    }

    return (
        <>
            <>
                {showError &&
                    <Snackbar
                        variant="soft"
                        color="danger"
                        open={showError}
                        onClose={() => setShowError(false)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        startDecorator={<Report />}
                        endDecorator={
                            <Button
                                onClick={() => setShowError(false)}
                                size="sm"
                                variant="soft"
                                color="danger"
                            >
                                Dismiss
                            </Button>
                        }
                    >
                        {error}
                    </Snackbar>
                }
                {showSuccess &&
                    <Snackbar
                        variant="soft"
                        color="success"
                        open={showSuccess}
                        onClose={() => setShowSuccess(false)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        startDecorator={<Report />}
                        endDecorator={
                            <Button
                                onClick={() => setShowSuccess(false)}
                                size="sm"
                                variant="soft"
                                color="success"
                            >
                                Dismiss
                            </Button>
                        }
                    >
                        {success}
                    </Snackbar>
                }
            </>
            <CreateAutomationPopup
                subscription={subscription}
                showAddNewAutomationModal={showAddNewAutomationModal}
                onClose={() => setShowAddNewAutomationModal(false)}
                app={app}
                didSaveNewAutomation={() => {
                    getAutomations();
                    didSaveNewAutomation();
                }}
            />
            {/* Reply Automations modal */}
            < Modal
                open={showAutomationsModal}
                onClose={() => onClose()}>
                <ModalOverflow>
                    {loading ?
                        <LoadingSpinnerComponent />
                        :
                        <ModalDialog
                            color="neutral"
                            layout="center"
                            size="sm"
                            variant="plain"
                            maxWidth={700}
                            minWidth={700}>
                            <ModalClose />
                            <DialogTitle>
                                Manage your automations
                            </DialogTitle>
                            <Divider />
                            <Typography
                                level="body-sm">
                                Automating your replies to user reviews can help you:
                                <ul className='mt-1'>
                                    <li>Improve your app store ranking. Regular responses help boost visibility and user trust.</li>
                                    <li>Automate responses to save time and ensure consistency.</li>
                                </ul>
                            </Typography>
                            <Divider />
                            <DialogContent>
                                {automations.length === 0 &&
                                    <Alert color="warning">
                                        No automations found. Create your first automation to get started!
                                    </Alert>
                                }
                                {automations.length > 0 &&
                                    <>
                                        <Typography
                                            level="body-sm"
                                            fontWeight={900}
                                            sx={{
                                                marginBottom: 1
                                            }}>
                                            Saved Automations:
                                        </Typography>
                                        {automations.map((automation: Automation, index: number) => (
                                            <div className="rounded bg-doriean mb-2 p-3">
                                                <div className='row' key={index}>
                                                    <div className='col'>
                                                        <div className="flex row gap-2 p-2 mb-2">
                                                            <Chip
                                                                size="sm"
                                                                color={automation.is_active ? "success" : "danger"}>
                                                                {automation.is_active ? "Active" : "Inactive"}
                                                            </Chip>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col">
                                                                <Typography
                                                                    level="body-sm"
                                                                    key={index}
                                                                    gutterBottom>
                                                                    {automation.name}
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='col-auto my-auto'>
                                                        <Button
                                                            color="danger"
                                                            variant="outlined"
                                                            size="sm"
                                                            sx={{ marginRight: 1 }}
                                                            onClick={() => deleteAutomation(automation.id)}>
                                                            Delete
                                                        </Button>
                                                        <Button
                                                            color="neutral"
                                                            variant="outlined"
                                                            size="sm"
                                                            onClick={() => disableAutomation(automation)}>
                                                            {automation.is_active ? "Disable" : "Enable"}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                }

                                <Button
                                    sx={{ mt: 2 /* margin top */ }}
                                    onClick={() => setShowAddNewAutomationModal(true)}>
                                    Create New Automation
                                </Button>
                            </DialogContent >
                        </ModalDialog>
                    }
                </ModalOverflow>
            </Modal >
        </>
    );
}

export default ReplyAutomationsPopup;